import React, { useState } from "react";
import { formatPhoneNumber } from "../shared/formatter";
import PhoneInput from "../components/PhoneInput";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Link as MuiLink,
  Typography,
  Grid,
  Chip,
  Stack,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import OrciBanner from "../components/OrciBanner"; // Assuming this is your logo component
import Footer from "../components/Footer";
import decodeJwtToken from "../auth/decodeJwtToken";
import { motion } from "framer-motion";
import { useBranch } from "../contexts/BranchContext";
import { useAuth } from "../contexts/AuthContext";
import swal from "sweetalert";
import { loginUser } from "../services/authService";
import { getPostAuthRoute } from "../shared/systemAccessRouting";

function Login({ onLogin }) {
  const navigate = useNavigate();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const [errors, setErrors] = useState({});
  const { setBranch } = useBranch();
  const { refreshUser } = useAuth();

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event) => event.preventDefault();
  const handleSubmit = async (event) => {
    event.preventDefault();
    setShowSpinner(true);
    setErrors({}); // Clear previous errors

    // --- Inline Validation ---
    const newErrors = {};
    if (!phoneNumber) {
      newErrors.phoneNumber = "Phone number is required.";
    }
    if (!password) {
      newErrors.password = "Password is required.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setShowSpinner(false);
      return;
    }

    const formattedPhone = formatPhoneNumber(phoneNumber);

    try {
      const data = await loginUser(formattedPhone, password);
      localStorage.setItem("accessToken", data.access);
      localStorage.setItem("refreshToken", data.refresh);
      const decodedToken = decodeJwtToken(data.access);

      refreshUser();

      onLogin();
      swal({
        text: "Successful Login!",
        icon: "success",
        title: "Auth",
        timer: 2000,
        buttons: false,
      }).then(() => {
        setPhoneNumber("");
        setPassword("");
        const allBranch = { id: "", name: "All" };
        const defaultBranch = ["General Manager", "Admin"].includes(
          decodedToken?.group?.name
        )
          ? allBranch
          : decodedToken?.branch;
        setBranch(defaultBranch);
        const nextRoute = getPostAuthRoute(decodedToken);
        const routeState = nextRoute === "/app/my-groups" ? { fromLogin: true } : undefined;
        navigate(nextRoute, routeState ? { state: routeState } : undefined);
      });
    } catch (error) {
      swal({
        title: "Login Failed",
        text: error.message,
        icon: "error",
        timer: 2000,
        buttons: false,
      });
    } finally {
      setShowSpinner(false);
    }
  };

  // Animation variants for framer-motion
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 },
  };

  const valueIndicators = [
    { label: "Contributions", icon: <SavingsOutlinedIcon fontSize="small" /> },
    { label: "Loans", icon: <AccountBalanceWalletOutlinedIcon fontSize="small" /> },
    { label: "Approvals", icon: <VerifiedUserOutlinedIcon fontSize="small" /> },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        overflowX: "hidden",
        bgcolor: "#fbf7f9",
        background:
          "radial-gradient(circle at 20% 24%, rgba(128, 11, 51, 0.1) 0, rgba(128, 11, 51, 0.035) 28%, transparent 46%), radial-gradient(circle at 78% 72%, rgba(45, 109, 143, 0.12) 0, rgba(45, 109, 143, 0.03) 30%, transparent 48%), linear-gradient(180deg, #fbf7f9 0%, #f5f7fb 100%)",
      }}
    >
      <Grid
        container
        component="main"
        sx={{
          position: "relative",
          flex: 1,
          alignItems: "center",
          justifyContent: "center",
          px: { xs: 2, sm: 4, lg: 7 },
          py: { xs: 3, md: 5 },
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            opacity: 0.45,
            backgroundImage:
              "linear-gradient(rgba(128, 11, 51, 0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(128, 11, 51, 0.05) 1px, transparent 1px)",
            backgroundSize: "44px 44px",
            pointerEvents: "none",
          },
        }}
      >
        {/* LEFT Branding */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            position: "relative",
            color: "#24171f",
            display: { xs: "none", md: "flex" },
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "flex-start",
            px: { md: 4, lg: 7, xl: 10 },
            py: 5,
          }}
        >
          <Box sx={{ position: "relative", zIndex: 1, maxWidth: 640 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 5 }}>
            <Box>
              <Typography variant="overline" sx={{ color: "#800b33", letterSpacing: 1.2 }}>
                Savvy Community
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1, color: "#352631" }}>
                Digital SACCOS Platform
              </Typography>
            </Box>
          </Stack>

          <Typography
            variant="h2"
            sx={{
              fontWeight: 900,
              lineHeight: 1.08,
              mb: 2.5,
              fontSize: { md: "3rem", lg: "3.65rem" },
              color: "#20151b",
            }}
          >
            Manage Your Community Finances with Confidence
          </Typography>

          <Typography
            variant="h6"
            sx={{
              color: "#655864",
              fontWeight: 400,
              lineHeight: 1.7,
              maxWidth: 560,
              mb: 4,
            }}
          >
            Manage contributions, loans, approvals and group finances securely in one place.
          </Typography>

          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 5 }}>
            {valueIndicators.map((item) => (
              <Chip
                key={item.label}
                icon={item.icon}
                label={item.label}
                sx={{
                  color: "#5d1534",
                  borderColor: "rgba(128,11,51,0.18)",
                  bgcolor: "rgba(255,255,255,0.72)",
                  px: 0.5,
                  boxShadow: "0 10px 25px rgba(77, 38, 55, 0.08)",
                  "& .MuiChip-icon": { color: "#800b33" },
                }}
                variant="outlined"
              />
            ))}
          </Stack>

          <Paper
            elevation={0}
            sx={{
              maxWidth: 520,
              p: 3,
              borderRadius: 3,
              bgcolor: "rgba(255,255,255,0.76)",
              border: "1px solid rgba(128,11,51,0.09)",
              color: "#24171f",
              backdropFilter: "blur(16px)",
              boxShadow: "0 22px 55px rgba(56, 32, 43, 0.1)",
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: "rgba(128,11,51,0.08)",
                  color: "#800b33",
                }}
              >
                <GroupsOutlinedIcon />
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  Built for group financial leadership
                </Typography>
                <Typography variant="body2" sx={{ color: "#736771" }}>
                  Clear member records, controlled approvals, and accountable money movement.
                </Typography>
              </Box>
            </Stack>
          </Paper>
          </Box>
        </Grid>

        {/* RIGHT LOGIN PANEL */}
        <Grid
          item
          xs={12}
          sm={12}
          md={5}
          component={Paper}
          elevation={0}
          square
          sx={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            flexDirection: "column",
            bgcolor: "transparent",
          }}
        >
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            px: { xs: 2.5, sm: 5, lg: 7 },
            pt: { xs: 4, md: 6 },
            pb: { xs: 2, md: 3 },
          }}
        >
          <Box
            sx={{
              display: { xs: "block", md: "none" },
              textAlign: "center",
              mb: 2,
              maxWidth: 360,
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 900, color: "#800b33", mb: 0.5 }}>
              Savvy Community
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Secure tools for contributions, loans and group finances.
            </Typography>
          </Box>

          {/* LOGO */}
          <motion.div variants={itemVariants}>
            <Paper
              elevation={6}
              sx={{
                p: 2,
                borderRadius: "50%",
                mb: 2,
                width: { xs: 104, sm: 112 },
                height: { xs: 104, sm: 112 },
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                bgcolor: "white",
                boxShadow: "0 18px 38px rgba(45, 16, 30, 0.16)",
              }}
            >
              <OrciBanner />
            </Paper>
          </motion.div>

          <motion.div variants={itemVariants}>
            <Typography
              component="h1"
              variant="h5"
              sx={{ mt: 1, fontWeight: 800, color: "#20151b" }}
            >
              Welcome Back
            </Typography>
          </motion.div>

          <motion.div variants={itemVariants}>
            <Typography color="text.secondary" sx={{ mb: 3, textAlign: "center" }}>
              Login to continue managing your group
            </Typography>
          </motion.div>

          {/* FORM */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            style={{ width: "100%", maxWidth: 520 }}
          >
            <Paper
              elevation={4}
              sx={{
                width: "100%",
                p: { xs: 2.5, sm: 4 },
                borderRadius: 3,
                background: "rgba(255,255,255,0.94)",
                border: "1px solid rgba(128, 11, 51, 0.1)",
                boxShadow: "0 22px 55px rgba(56, 32, 43, 0.14)",
              }}
            >
              <Box component="form" noValidate onSubmit={handleSubmit}>
                {/* PHONE */}
                <motion.div variants={itemVariants}>
                  <PhoneInput
                    margin="normal"
                    fullWidth
                    id="phone_number"
                    value={phoneNumber}
                    onChange={(val) => setPhoneNumber(val)}
                    error={!!errors.phoneNumber}
                    helperText={errors.phoneNumber}
                    required
                  />
                </motion.div>

                {/* PASSWORD */}
                <motion.div variants={itemVariants}>
                  <TextField
                    margin="normal"
                    variant="outlined"
                    required
                    fullWidth
                    id="password"
                    label="Password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    error={!!errors.password}
                    helperText={errors.password}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlinedIcon color="primary" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                            aria-label={showPassword ? "Hide password" : "Show password"}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </motion.div>

                <motion.div variants={itemVariants}>
                  <Box textAlign="right" sx={{ my: 1 }}>
                    <MuiLink
                      component={RouterLink}
                      to="/forgot-password"
                      variant="body2"
                      sx={{ color: "#800b33", fontWeight: 700 }}
                    >
                      Forgot Password?
                    </MuiLink>
                  </Box>
                </motion.div>

                {/* LOGIN BUTTON */}
                <motion.div variants={itemVariants}>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{
                      mt: 3,
                      mb: 2,
                      py: 1.4,
                      borderRadius: "10px",
                      fontWeight: "bold",
                      fontSize: "1rem",
                      bgcolor: "#800b33",
                      boxShadow: "0 12px 24px rgba(128, 11, 51, 0.24)",
                      "&:hover": { bgcolor: "#6c0a2b" },
                    }}
                    disabled={showSpinner}
                  >
                    {showSpinner ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Login"
                    )}
                  </Button>
                </motion.div>
              </Box>
            </Paper>
          </motion.div>
        </Box>
        </Grid>
      </Grid>
      <Footer />
    </Box>
  );

}

export default Login;
